import { apiFetch } from "@/integrations/supabase/client";
import type { Stem } from "@/types/stem";
import { calculateProcessingTime } from "@/utils/audioUtils";
import { setSessionIdHeader, getSessionId } from "@/api/sessionApi";

export const MAX_AUDIO_DURATION_SECONDS = 15 * 60;
export const MAX_AUDIO_DURATION_MESSAGE = "Audio files must be 15 minutes or shorter.";

export const getAudioDuration = async (audioUrl: string): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();

    const cleanup = () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("error", handleError);
    };
    const handleLoadedMetadata = () => {
      cleanup();
      if (!Number.isFinite(audio.duration) || audio.duration <= 0) {
        reject(new Error("Unable to determine the audio duration."));
        return;
      }
      resolve(audio.duration);
    };
    const handleError = () => {
      cleanup();
      reject(new Error("Unable to read this audio file. Please try MP3, WAV, M4A, or AAC."));
    };

    audio.preload = "metadata";
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("error", handleError);
    audio.src = audioUrl;
  });
};

export const assertSupportedAudioDuration = (durationSeconds: number): void => {
  if (durationSeconds > MAX_AUDIO_DURATION_SECONDS) {
    throw new Error(MAX_AUDIO_DURATION_MESSAGE);
  }
};

export const getProcessingDuration = async (audioUrl: string): Promise<number> => {
  const duration = await getAudioDuration(audioUrl);
  assertSupportedAudioDuration(duration);
  return calculateProcessingTime(duration);
};

export const uploadFile = async (file: File): Promise<string> => {
  // Ensure session is set before uploading
  await setSessionIdHeader();
  const sessionId = getSessionId();

  console.log("Uploading file via secure Backblaze upload:", file.name);

  try {
    // Step 1: Get presigned URL from the API
    const formData = new FormData();
    formData.append("file", file);

    const res = await apiFetch("/upload", {
      method: "POST",
      body: formData,
    });
    const data = await res.json();

    if (!data || !data.success) {
      console.error("Presign failed:", data);
      throw new Error(`Presign failed: ${data?.error || "Unknown error"}`);
    }

    console.log("Got presigned URL, uploading directly to Backblaze...");

    // Step 2: Upload directly to Backblaze using presigned URL
    const uploadResponse = await fetch(data.url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      throw new Error(`Direct upload failed: ${uploadResponse.status} ${errorText}`);
    }

    console.log("File uploaded successfully to Backblaze via presigned URL");
    return data.publicUrl; // Return the final public URL
  } catch (error) {
    console.error("Error in uploadFile:", error);
    throw error;
  }
};

export const processStemSplit = async (
  audioUrl: string,
  mode: "aggregate-6stem-v2" | "aggregate-vocals" | "aggregate-drumsep" = "aggregate-6stem-v2",
): Promise<Stem[]> => {
  // Ensure session is set before making the function call
  const sessionId = await setSessionIdHeader();
  console.log("Processing stems with session ID:", sessionId);
  console.log("Using mode:", mode);

  // Keep the browser request alive for the longest supported input plus finalization.
  const timeoutDuration = 16 * 60 * 1000;
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error("Processing timeout: The operation took too long to complete"));
    }, timeoutDuration);
  });

  try {
    // Race between the processing and the timeout
    const responsePromise = apiFetch("/process", {
      method: "POST",
      body: JSON.stringify({ audioUrl, mode }),
    }).then((res) => res.json());

    const data = (await Promise.race([responsePromise, timeoutPromise])) as any;

    if (data?.error) {
      console.error("Stem processing failed:", data);
      if (typeof data.details === "string" && data.details.includes("maximum is 15 minutes")) {
        throw new Error(MAX_AUDIO_DURATION_MESSAGE);
      }
      throw new Error(typeof data.error === "string" ? data.error : "Stem processing failed");
    }

    if (!data?.result || !Array.isArray(data.result)) {
      console.error("Invalid response from process-stems:", data);
      throw new Error("Invalid response from stem processing service");
    }

    // Get audio duration
    const audio = new Audio(audioUrl);
    await new Promise((resolve) => {
      audio.addEventListener("loadedmetadata", () => {
        resolve(audio.duration);
      });
    });
    const duration = audio.duration;

    console.log("Received stem processing result:", data.result);

    // Map the new API response to the Stem[] format
    const stems: Stem[] = data.result.map((item: any) => ({
      name: item.instrument.replace("_", " ").replace(/\b\w/g, (char) => char.toUpperCase()), // Format instrument name
      url: item.public_url,
      duration,
    }));

    // Prepend the "Original" stem
    stems.unshift({
      name: "Original",
      url: audioUrl,
      duration,
    });

    // Remove any duplicates by URL
    const uniqueStems = stems.filter((stem, index, self) => index === self.findIndex((s) => s.url === stem.url));

    // Verify we have at least two stems (original + at least one other)
    if (uniqueStems.length < 2) {
      console.error("Missing stems in result:", data.result);
      throw new Error("Processing service did not return the required stems");
    }

    return uniqueStems;
  } catch (error) {
    console.error("Error in processStemSplit:", error);
    if (error instanceof Error && error.message.includes("timeout")) {
      throw new Error("The stem processing took too long to complete. Please try again or use a shorter audio file.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId!);
  }
};
